/**
 * Org-scoped inboxes (BAL-580).
 *
 * A conversation with `organization_id` set is addressed to an organization.
 * Two people look at it differently:
 *   - the external person who wrote to the org sees ONLY the org (name and
 *     initials) on every incoming message, never the member who replied;
 *   - an org member sees the external person as the counterpart, and sees
 *     which member wrote each reply that went out as the org.
 *
 * The inbox is scoped to the active workspace: an org workspace shows only
 * that org's threads, the personal workspace shows everything else. Pure
 * functions, so the scoping rule is testable without a database.
 */

/** Which inbox is being looked at. */
export type InboxScope =
  | { kind: 'personal' }
  | { kind: 'organization'; organizationId: string };

export const PERSONAL_INBOX_SCOPE: InboxScope = { kind: 'personal' };

interface OrgScopedConversation {
  organization_id?: string | null;
}

/**
 * TRUE when the viewer answers this thread on behalf of its org: the thread is
 * addressed to an org and the viewer is a member of that org.
 */
export function isOrgSideViewer(
  conversation: OrgScopedConversation,
  myOrganizationIds: readonly string[],
): boolean {
  const orgId = conversation.organization_id;
  return !!orgId && myOrganizationIds.includes(orgId);
}

/**
 * Does this conversation belong in the given inbox?
 *
 * - org inbox: only threads addressed to that org;
 * - personal inbox: every thread EXCEPT those the viewer answers for one of
 *   their orgs. A thread the viewer opened TO an org is their own
 *   conversation and stays personal.
 */
export function isConversationInScope(
  conversation: OrgScopedConversation,
  scope: InboxScope,
  myOrganizationIds: readonly string[],
): boolean {
  if (scope.kind === 'organization') {
    return conversation.organization_id === scope.organizationId;
  }

  return !isOrgSideViewer(conversation, myOrganizationIds);
}

/** Two-letter initials for an org name ("Ballet Zurich" -> "BZ"). */
export function getOrganizationInitials(name: string | null | undefined) {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return 'O';
  }

  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase();
  }

  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/**
 * PostgREST embeds that every message read adds next to `sender` (BAL-580):
 * the organization an organization message is from, and the member who wrote
 * it. RLS returns `org_author` to members of that organization only.
 */
export const MESSAGE_ORG_SENDER_EMBEDS = `
  sender_organization:organizations!messages_sender_organization_id_fkey(id, name, logo_media_id),
  org_author:message_org_authors(author_user_id, author:profiles!message_org_authors_author_user_id_fkey(id, first_name, last_name, profile_photo_url, is_deleted, deleted_at, auth_user_id, is_merged))
`;

/** The fields of a message that say who it is from. */
interface MessageSenderFields {
  sender_id: string | null;
  sender_kind?: string | null;
  sender_organization_id?: string | null;
  org_author?: { author_user_id: string } | null;
}

/** TRUE when the message was sent as an organization (BAL-580). */
export function isOrganizationMessage(message: MessageSenderFields): boolean {
  return message.sender_kind === 'organization';
}

/**
 * The person who wrote the message, as far as the viewer may know. For an
 * organization message that is the member in message_org_authors, which RLS
 * only returns to org members; everyone else gets NULL.
 */
export function getMessageAuthorId(
  message: MessageSenderFields,
): string | null {
  if (isOrganizationMessage(message)) {
    return message.org_author?.author_user_id ?? null;
  }

  return message.sender_id;
}

/**
 * A stable key for "same sender as the previous message" (avatar grouping).
 * Organization messages group by author when the viewer may see it, else by
 * the organization.
 */
export function getMessageSenderKey(message: MessageSenderFields): string {
  if (isOrganizationMessage(message)) {
    const authorId = message.org_author?.author_user_id;
    return authorId
      ? `org-author:${authorId}`
      : `org:${message.sender_organization_id ?? ''}`;
  }

  return `person:${message.sender_id ?? ''}`;
}

interface NotificationSenderInput {
  /** The member's own display name. */
  senderName: string;
  /** messages.sender_organization_id of an organization message, else null. */
  senderOrganizationId: string | null | undefined;
  /** organizations.name of that org. */
  organizationName: string | null | undefined;
  /** Is the recipient a current member of that org? */
  recipientIsOrgMember: boolean;
}

/** Shown when an org reply must be masked but the org's name is unknown. */
const FALLBACK_ORGANIZATION_NAME = 'Organization';

/**
 * Who a notification (push, email) says a message is from, for one recipient.
 * An organization message is shown as the org to everyone outside it: they
 * get the org's name and no photo. It fails closed: without the org's name the
 * mask still holds, with a generic label, never the member's name.
 */
export function resolveNotificationSender({
  senderName,
  senderOrganizationId,
  organizationName,
  recipientIsOrgMember,
}: NotificationSenderInput): { senderName: string; masked: boolean } {
  if (senderOrganizationId && !recipientIsOrgMember) {
    return {
      senderName: organizationName?.trim() || FALLBACK_ORGANIZATION_NAME,
      masked: true,
    };
  }

  return { senderName, masked: false };
}
